import React, { useRef, useState } from "react";
import { AvatarChip } from "./AvatarChip";
import { GloryBadgesGrid } from "./GloryBadgesGrid";
import { VerifiedCheck } from "./VerifiedCheck";
import { TipButton } from "./TipButton";
import { PlatformChips } from "./PlatformChips";
import { LiveBadge } from "./LiveBadge";
import { useAuth } from "../context/AuthContext";

/**
 * HoloPlayerCard — collectible Pokémon-style player card.
 *
 * Each card has:
 *  - A rarity tier driven by `user_type`/`plan` (COMMON / RARE / HOLO RARE).
 *  - A holographic shine layer that responds to pointer position.
 *  - A "PSA grade" frame stamp (MINT 26).
 *  - A stat block (MMR as HP), all `games_played` chips (no truncation),
 *    all teams (clickable), team badges, and Friend/MSG actions.
 *  - A serial number so it feels collectible (and future-proof for on-chain
 *    minting).
 *
 * Designed to keep the existing data-testids on player cards so other tests
 * keep working.
 */

const RARITY_BY_USER = (p) => {
  const plan = (p.plan || "").toLowerCase();
  const type = (p.user_type || "").toLowerCase();
  if (plan === "pro") return "HOLO_RARE";
  if (type === "pro") return "RARE";
  return "COMMON";
};

const RARITY_STYLES = {
  COMMON: {
    label: "COMMON",
    frame: "from-[#1a1a1f] via-[#1a1a1f] to-[#1a1a1f]",
    border: "border-[rgba(20,184,166,0.18)]",
    glow: "",
    accent: "text-white/65",
    accentBg: "bg-[rgba(20,184,166,0.06)] border-[rgba(20,184,166,0.18)]",
    chip: "bg-[rgba(20,184,166,0.06)] border-[rgba(20,184,166,0.18)] text-white/70",
    holo: false,
  },
  RARE: {
    label: "RARE",
    frame: "from-[#1a1a1f] via-[#1a1a1f] to-[#1a1a1f]",
    border: "border-[rgba(20,184,166,0.40)]",
    glow: "shadow-[0_0_18px_rgba(20,184,166,0.30)]",
    accent: "text-[#14b8a6]",
    accentBg: "bg-[rgba(20,184,166,0.10)] border-[rgba(20,184,166,0.40)]",
    chip: "bg-[rgba(20,184,166,0.10)] border-[rgba(20,184,166,0.40)] text-[#14b8a6]",
    holo: true,
  },
  HOLO_RARE: {
    label: "PRO",
    frame: "from-[#1a1a1f] via-[#1a1a1f] to-[#1a1a1f]",
    border: "border-[rgba(20,184,166,0.55)]",
    glow: "shadow-[0_0_24px_rgba(20,184,166,0.40)]",
    accent: "text-[#14b8a6]",
    accentBg: "bg-[rgba(20,184,166,0.14)] border-[rgba(20,184,166,0.55)]",
    chip: "bg-[rgba(20,184,166,0.14)] border-[rgba(20,184,166,0.55)] text-[#14b8a6]",
    holo: true,
  },
};

// Deterministic 5-digit "serial number" from user_id. Reads like a mint #.
function serialOf(userId) {
  let h = 0;
  for (let i = 0; i < (userId || "").length; i++) {
    h = (h * 31 + userId.charCodeAt(i)) >>> 0;
  }
  return String(h % 99999).padStart(5, "0");
}

export function HoloPlayerCard({
  p,
  isSelf,
  friendStatus,
  busyFriend,
  onSendFriend,
  onOpenDm,
  onOpenLeague,
  onReport,
  onReportImage,
  onGiftPro,
}) {
  const rarity = RARITY_BY_USER(p);
  const style = RARITY_STYLES[rarity];
  const friendLabel =
    friendStatus === "friends" ? "🤝 FRIENDS"
    : friendStatus === "request_sent" ? "✉ SENT"
    : friendStatus === "request_received" ? "✓ ACCEPT"
    : "+ FRIEND";
  const friendDisabled = friendStatus === "friends" || friendStatus === "request_sent";

  // Pointer-driven holo shine.
  const cardRef = useRef(null);
  const [shine, setShine] = useState({ x: 50, y: 50, tx: 0, ty: 0 });
  const onMove = (e) => {
    if (!style.holo) return;
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    // Subtle 3D tilt: -6° to +6° on each axis.
    const tx = ((y - 50) / 50) * -6;
    const ty = ((x - 50) / 50) * 6;
    setShine({ x, y, tx, ty });
  };
  const onLeave = () => setShine({ x: 50, y: 50, tx: 0, ty: 0 });

  const serial = serialOf(p.user_id);

  // Custom card background — one of 6 neon presets OR a `data:image/...` upload.
  // Falls back to the rarity gradient when card_bg is not set.
  const CARD_BG_PRESETS = {
    cyan:    "linear-gradient(135deg, #001a26 0%, #14b8a644 100%)",
    magenta: "linear-gradient(135deg, #1a001a 0%, #14b8a644 100%)",
    violet:  "linear-gradient(135deg, #0d0024 0%, #14b8a644 100%)",
    lime:    "linear-gradient(135deg, #001a0d 0%, #14b8a644 100%)",
    orange:  "linear-gradient(135deg, #1a0d00 0%, #ff9d4244 100%)",
    slate:   "linear-gradient(135deg, #050505 0%, #1a1a1f 100%)",
  };
  const customBgStyle = (() => {
    const v = p.card_bg;
    if (!v) return null;
    if (v.startsWith("preset:")) {
      return { background: CARD_BG_PRESETS[v.slice(7)] || undefined };
    }
    if (v.startsWith("data:") || v.startsWith("http")) {
      return { backgroundImage: `url(${v})`, backgroundSize: "cover", backgroundPosition: "center" };
    }
    // Plain CSS color
    return { background: v };
  })();

  return (
    <div
      ref={cardRef}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      data-testid={`player-card-${p.user_id}`}
      className={`holo-card relative rounded-[22px] overflow-hidden ${customBgStyle ? "" : `bg-gradient-to-br ${style.frame}`} border-2 ${style.border} ${style.glow} transition-transform duration-200`}
      style={{
        transform: `perspective(900px) rotateX(${shine.tx}deg) rotateY(${shine.ty}deg)`,
        ...(customBgStyle || {}),
        transformStyle: "preserve-3d",
      }}
    >
      {/* Holographic shine overlay — pointer-driven on RARE+, otherwise idle. */}
      {style.holo && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 mix-blend-screen opacity-70"
          style={{
            background: `radial-gradient(circle at ${shine.x}% ${shine.y}%, rgba(255,255,255,0.65), rgba(255,255,255,0) 45%), repeating-linear-gradient(115deg, rgba(255,255,255,0.05) 0px, rgba(255,255,255,0.05) 2px, transparent 2px, transparent 6px)`,
          }}
        />
      )}
      {/* Card-background picker — PRO-only perk, shown to the owner. */}
      {isSelf && (p.user_type === "pro" || p.plan === "pro" || p.plan === "elite") && (
        <CardBgPicker userId={p.user_id} current={p.card_bg} />
      )}
      {/* Subtle noise texture for "card stock" feel */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.4'/%3E%3C/svg%3E\")",
        }}
      />

      <div className="relative z-10 p-4 bg-black/70 m-[3px] rounded-[18px] flex flex-col gap-3">
        {/* Top bar: rarity + serial */}
        <div className="flex items-center justify-between text-[9px] tracking-[3px]">
          <span className={`px-2 py-0.5 rounded-md border ${style.accentBg} ${style.accent} font-bold`}>
            {style.label}
          </span>
          <span className="text-white/40 font-mono">
            #{serial} · MINT 26
          </span>
        </div>

        {/* Photo + identity */}
        <div className="flex items-start gap-3">
          <div className={`relative rounded-2xl p-1 bg-gradient-to-br ${style.frame} border ${style.border}`}>
            <AvatarChip avatarId={p.avatar_id} pfpUrl={p.pfp_url} size={64} />
            {/* Inline "report image" flag — only when not viewing your own
                card and the user has a custom-uploaded pfp_url (presets are
                safe by definition, so no point reporting them). */}
            {!isSelf && p.pfp_url && onReportImage && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onReportImage(p, "player_pfp");
                }}
                data-testid={`player-report-pfp-${p.user_id}`}
                title="Report this profile picture"
                aria-label="Report this profile picture"
                className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-black/85 border border-[#14b8a6]/50 text-[#14b8a6] hover:bg-[#14b8a6] hover:text-black transition-colors flex items-center justify-center text-[10px] shadow-lg"
              >
                <i className="fas fa-flag" aria-hidden="true" />
              </button>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display text-lg leading-tight truncate flex items-center gap-1.5" data-testid={`player-card-name-${p.user_id}`}>
              <a
                href={p.username ? `/u/${p.username}` : `/players/${p.user_id}`}
                data-testid={`player-card-name-link-${p.user_id}`}
                onClick={(e) => e.stopPropagation()}
                className="truncate hover:text-[#14b8a6] transition-colors"
              >
                {p.name}
              </a>
              <VerifiedCheck user={p} size={16} />
            </p>
            <p className="text-[11px] text-[#a3a3ac] truncate">
              {p.game}{p.user_type === "pro" ? " · PRO" : ""}
            </p>
            {p.live && (
              <div className="mt-1.5">
                <LiveBadge live={p.live} size="sm" dataTestid={`holo-card-live-${p.user_id}`} />
              </div>
            )}
            <div className="mt-1.5 flex gap-2">
              <span
                className={`relative group/mmr text-[10px] tracking-widest px-2 py-0.5 rounded-md border ${style.accentBg}`}
                data-testid="holo-card-mmr-chip"
                tabIndex={0}
                aria-label="MMR — how it's calculated"
              >
                <span className={`${style.accent} font-bold`}>{p.elo}</span>
                <span className="text-white/40 ml-1">MMR</span>
                <i className="fas fa-circle-info text-white/40 ml-1 text-[9px]" aria-hidden="true" />
                <span
                  data-testid="holo-card-mmr-tooltip"
                  role="tooltip"
                  className="pointer-events-none absolute left-1/2 -translate-x-1/2 -bottom-1 translate-y-full z-30 w-56 rounded-xl border border-[#14b8a6]/40 bg-black/95 px-3 py-2 text-[10px] tracking-normal normal-case text-white/85 leading-snug shadow-[0_8px_24px_rgba(0,0,0,0.6)] opacity-0 group-hover/mmr:opacity-100 group-focus-within/mmr:opacity-100 transition-opacity"
                >
                  <span className="block text-[9px] tracking-[2px] text-[#14b8a6] font-bold mb-1">HOW MMR WORKS</span>
                  Calculated from your wins, losses &amp; tournament finishes — earned on the ladder, never self-claimed.
                </span>
              </span>
              <span className="text-[10px] tracking-widest px-2 py-0.5 rounded-md border border-white/15 bg-black/40 text-white/70">
                {(p.user_type || "casual").toUpperCase()}
              </span>
              {/* Self-attestation badge — opt-in flair set in
                  Profile → SOCIAL tab. At most one of dad/mom can be
                  active (the toggles are mutually exclusive). */}
              {(p.is_certified_dad || p.is_certified_mom) && (
                <span
                  data-testid={`holo-card-certified-${p.is_certified_dad ? "dad" : "mom"}`}
                  title={p.is_certified_dad
                    ? "Self-attested CERTIFIED DAD"
                    : "Self-attested CERTIFIED MOM"}
                  className="text-[10px] tracking-widest px-2 py-0.5 rounded-md border border-[#14b8a6]/40 bg-[#14b8a6]/10 text-[#14b8a6] font-bold"
                >
                  <i className={`fas ${p.is_certified_dad ? "fa-mug-hot" : "fa-heart"} mr-1`} />
                  {p.is_certified_dad ? "CERTIFIED DAD" : "CERTIFIED MOM"}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Top 3 games — uses score_event aggregate when present, else falls back to games_played[:3] */}
        {(p.top_games?.length > 0 || p.games_played?.length > 0) && (
          <div data-testid={`player-games-${p.user_id}`}>
            <p className="text-[9px] tracking-[3px] text-[#14b8a6] mb-1">
              <i className="fas fa-gamepad mr-1" />TOP GAMES
            </p>
            <div className="flex flex-wrap gap-1">
              {(p.top_games && p.top_games.length > 0
                ? p.top_games
                : (p.games_played || []).slice(0, 3).map((g) => ({ game: g, matches: 0 }))
              ).slice(0, 3).map((tg, idx) => (
                <span
                  key={tg.game}
                  data-testid={`player-game-${idx}`}
                  className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-md bg-[#14b8a6]/10 border border-[#14b8a6]/30 text-[#14b8a6]"
                >
                  {idx < 3 && <span className="font-bold">#{idx + 1}</span>}
                  <span>{tg.game}</span>
                  {tg.matches > 0 && <span className="text-white/45 font-mono">{tg.matches}m</span>}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Connected platforms — Steam / Xbox / PSN / Epic.
            Discord is shown elsewhere on the holo card so we hide it
            here to avoid duplicate chips. Empty state only renders for
            the card owner so they see the "add your handles" nudge. */}
        {(() => {
          const platforms = [p.steam_handle, p.xbox_handle, p.psn_handle, p.epic_handle];
          const hasAny = platforms.some((h) => (h || "").trim());
          if (!hasAny && !isSelf) return null;
          return (
            <div data-testid={`player-platforms-${p.user_id}`}>
              <p className="text-[9px] tracking-[3px] text-[#14b8a6] mb-1">
                <i className="fas fa-link mr-1" />CONNECTED
              </p>
              <PlatformChips
                source={p}
                compact
                hideDiscord
                showEmpty={!!isSelf}
                testIdPrefix={`player-platforms-chips-${p.user_id}`}
              />
            </div>
          );
        })()}

        {/* Teams — ALL of them, each clickable to open the parent league */}
        {p.teams?.length > 0 && (
          <div data-testid={`player-teams-${p.user_id}`}>
            <p className="text-[9px] tracking-[3px] text-[#14b8a6] mb-1">
              <i className="fas fa-shield-halved mr-1" />TEAMS · {p.teams.length}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {p.teams.map((t) => (
                <button
                  key={t.team_id}
                  type="button"
                  onClick={() => t.league_id && onOpenLeague?.(t.league_id)}
                  data-testid={`player-team-chip-${p.user_id}-${t.team_id}`}
                  title={`${t.name}${t.league_name ? ` · ${t.league_name}` : ""}${t.is_manager ? " · MANAGER" : ""}`}
                  className={`inline-flex items-center gap-1 text-[10px] tracking-wide px-2 py-1 rounded-lg bg-black/40 border hover:brightness-125 hover:-translate-y-0.5 transition ${
                    t.is_manager ? "border-[#14b8a6]/50 text-[#14b8a6]" : "border-[#14b8a6]/30 text-[#a3a3ac]"
                  }`}
                >
                  {t.is_manager && <i className="fas fa-crown text-[9px]" />}
                  <i className="fas fa-shield-halved text-[9px]" />
                  <span className="truncate max-w-[160px]">{t.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Team badges as collectibles */}
        {p.team_badges?.length > 0 && (
          <div data-testid={`player-badges-${p.user_id}`}>
            <p className="text-[9px] tracking-[3px] text-[#14b8a6] mb-1">
              <i className="fas fa-medal mr-1" />BADGES · {p.team_badges.length}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {p.team_badges.map((b) => {
                const tier = (b.tier || "bronze").toLowerCase();
                const ring = tier === "diamond" ? "border-[#14b8a6]"
                  : tier === "gold" ? "border-[#14b8a6]"
                  : tier === "silver" ? "border-slate-400"
                  : "border-[#14b8a6]";
                const txt = tier === "diamond" ? "text-[#14b8a6]"
                  : tier === "gold" ? "text-[#14b8a6]"
                  : tier === "silver" ? "text-slate-300"
                  : "text-[#14b8a6]0";
                return (
                  <span
                    key={b.code}
                    title={`${b.name} — ${tier.toUpperCase()}`}
                    className={`w-7 h-7 rounded-full border-2 ${ring} bg-black/50 flex items-center justify-center text-[11px] ${txt}`}
                  >
                    <i className={`fas ${b.icon || "fa-medal"}`} />
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Glory Badges — compact strip of unlocked achievements */}
        {p.user_id && (
          <div data-testid={`player-glory-${p.user_id}`} className="pt-1">
            <p className="text-[9px] tracking-[3px] text-[#14b8a6] mb-1">
              <i className="fas fa-crown mr-1" />GLORY
            </p>
            <GloryBadgesGrid userId={p.user_id} compact />
            {Number(p.verified_tournament_wins?.count || 0) > 0 && (
              <span
                data-testid={`player-verified-trophy-${p.user_id}`}
                title={p.verified_tournament_wins?.last_tournament_name
                  ? `Last win: ${p.verified_tournament_wins.last_tournament_name}` : ""}
                className="inline-flex items-center gap-1 mt-2 px-2 py-1 rounded-full bg-[#ffe066]/15 border border-[#ffe066]/50 text-[#ffe066] text-[10px] tracking-widest font-bold"
              >
                <i className="fas fa-trophy" />
                {p.verified_tournament_wins.count}× VERIFIED CHAMPION
              </span>
            )}
          </div>
        )}

        {/* Footer: actions OR mint stamp */}
        {!isSelf ? (
          <div className="space-y-1.5 pt-2 border-t border-white/10">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onSendFriend}
                disabled={friendDisabled || busyFriend}
                data-testid={`player-friend-btn-${p.user_id}`}
                className={`flex-1 text-[11px] tracking-widest px-3 py-1.5 rounded-xl border ${
                  friendStatus === "friends"
                    ? "bg-[#14b8a6]/15 text-[#14b8a6] border-[#14b8a6]/40"
                    : "bg-[#14b8a6]/15 text-[#14b8a6] border-[#14b8a6]/40 hover:bg-[#14b8a6]/25"
                } disabled:opacity-60 disabled:cursor-not-allowed`}
              >
                {busyFriend ? "..." : friendLabel}
              </button>
              {/* MSG button is always rendered so player cards stay
                  visually consistent. When the target has DMs off we
                  show the button disabled (greyed out + tooltip)
                  rather than hiding it entirely — hiding made the row
                  look broken to the viewer. */}
              {(() => {
                const dmsOff = p.messaging_policy === "off"
                  || p.messaging_enabled === false;
                const friendsOnly = p.messaging_policy === "friends";
                return (
                  <button
                    type="button"
                    onClick={dmsOff ? undefined : onOpenDm}
                    disabled={dmsOff}
                    title={dmsOff ? "This player has DMs disabled." : ""}
                    data-testid={`player-msg-btn-${p.user_id}`}
                    className={`flex-1 text-[11px] tracking-widest px-3 py-1.5 rounded-xl border transition-colors ${
                      dmsOff
                        ? "bg-white/5 text-white/30 border-white/10 cursor-not-allowed"
                        : "bg-[#14b8a6]/15 text-[#14b8a6] border-[#14b8a6]/40 hover:bg-[#14b8a6]/25"
                    }`}
                  >
                    <i className={`fas ${friendsOnly ? "fa-user-shield" : dmsOff ? "fa-envelope-circle-check" : "fa-envelope"} mr-1`} />
                    {dmsOff ? "DMS OFF" : friendsOnly ? "FRIENDS DM" : "MSG"}
                  </button>
                );
              })()}
            </div>
            <button
              type="button"
              onClick={() => onReport?.(p)}
              data-testid={`player-report-btn-${p.user_id}`}
              className="w-full text-[10px] tracking-widest text-white/50 hover:text-[#14b8a6] py-1 rounded-lg hover:bg-[#14b8a6]/10"
            >
              <i className="fas fa-flag mr-1" />REPORT PLAYER
            </button>
            {onGiftPro && (p.user_type || "").toLowerCase() !== "pro" && (
              <button
                type="button"
                onClick={() => onGiftPro?.(p)}
                data-testid={`player-gift-pro-btn-${p.user_id}`}
                className="w-full mt-1 text-[10px] tracking-widest text-[#14b8a6]/90 hover:text-[#14b8a6] py-1.5 rounded-lg border border-[#14b8a6]/30 hover:bg-[#14b8a6]/10 bg-[#14b8a6]/5"
                title="Gift 30 days of PRO Player to this operator"
              >
                <i className="fas fa-gift mr-1" />GIFT PRO PLAN · $1.99
              </button>
            )}
            <div className="mt-1">
              <TipButton recipient={p} compact />
            </div>
          </div>
        ) : (
          <div className="pt-2 border-t border-white/10 flex items-center justify-between gap-2 flex-wrap">
            <span className="text-[9px] tracking-[3px] text-white/40">
              <i className="fas fa-user-astronaut mr-1" />YOUR CARD
            </span>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={async () => {
                  // Web Share API first (mobile + modern desktop). On older
                  // browsers we fall through to Twitter/Discord buttons.
                  const profilePath = p.username ? `/u/${p.username}` : `/players/${p.user_id}`;
                  const shareUrl = `${window.location.origin}${profilePath}`;
                  const shareText = `Check my LEAGUE HUB holo card — MMR ${p.elo || "—"} · ${p.game || "multi-game"}`;
                  if (navigator.share) {
                    try {
                      await navigator.share({ title: `${p.name}'s LEAGUE HUB Card`, text: shareText, url: shareUrl });
                      return;
                    } catch (_) { /* user cancelled */ }
                  }
                  try { await navigator.clipboard.writeText(`${shareText} ${shareUrl}`); } catch (_) { /* ignore */ }
                }}
                data-testid={`player-card-share-${p.user_id}`}
                className="text-[10px] tracking-widest px-3 py-1.5 rounded-xl bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 hover:bg-[#14b8a6]/25"
              >
                <i className="fas fa-share-nodes mr-1" />SHARE
              </button>
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check my LEAGUE HUB holo card — MMR ${p.elo || "—"} · ${p.game || "multi-game"}`)}&url=${encodeURIComponent(`${window.location.origin}${p.username ? `/u/${p.username}` : `/players/${p.user_id}`}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`player-card-share-twitter-${p.user_id}`}
                title="Share to X / Twitter"
                className="text-[10px] tracking-widest px-3 py-1.5 rounded-xl bg-black/60 text-white border border-white/20 hover:bg-black/80"
              >
                <i className="fa-brands fa-x-twitter" />
              </a>
              <button
                type="button"
                onClick={async () => {
                  const profilePath = p.username ? `/u/${p.username}` : `/players/${p.user_id}`;
                  const shareUrl = `${window.location.origin}${profilePath}`;
                  const shareText = `Check my LEAGUE HUB holo card — MMR ${p.elo || "—"} · ${p.game || "multi-game"}\n${shareUrl}`;
                  try { await navigator.clipboard.writeText(shareText); } catch (_) { /* ignore */ }
                  window.open("https://discord.com/channels/@me", "_blank", "noopener,noreferrer");
                }}
                data-testid={`player-card-share-discord-${p.user_id}`}
                title="Copy link & open Discord — paste into any channel"
                className="text-[10px] tracking-widest px-3 py-1.5 rounded-xl bg-[#5865F2] text-white hover:brightness-110"
              >
                <i className="fa-brands fa-discord" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default HoloPlayerCard;


/**
 * CardBgPicker — small floating picker in the corner of the holo card.
 * 6 neon presets + image upload. Persists via PATCH /profile { card_bg }.
 * Stored values:
 *   - "preset:cyan|magenta|violet|lime|orange|slate" for the swatches
 *   - "data:image/..." for uploads (capped at 600 KB to stay inside Mongo doc limits)
 *   - "" (empty) to revert to the rarity-default gradient
 */
function CardBgPicker({ userId, current }) {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const { refresh } = useAuth();
  const PRESETS = [
    { id: "cyan",    swatch: "linear-gradient(135deg, #001a26, #14b8a6)",   label: "CYAN" },
    { id: "magenta", swatch: "linear-gradient(135deg, #1a001a, #14b8a6)",   label: "MAGENTA" },
    { id: "violet",  swatch: "linear-gradient(135deg, #0d0024, #14b8a6)",   label: "VIOLET" },
    { id: "lime",    swatch: "linear-gradient(135deg, #001a0d, #14b8a6)",   label: "LIME" },
    { id: "orange",  swatch: "linear-gradient(135deg, #1a0d00, #ff9d42)",   label: "ORANGE" },
    { id: "slate",   swatch: "linear-gradient(135deg, #050505, #303030)",   label: "SLATE" },
  ];

  const save = async (val) => {
    if (busy) return;
    setBusy(true);
    setErr("");
    try {
      const { updateProfile } = await import("../lib/api");
      await updateProfile({ card_bg: val });
      // Refresh the auth context so the new bg renders instantly without a
      // page reload (the picker is rendered inside HoloPlayerCard which is
      // driven by `user` from AuthContext).
      await refresh?.();
      setOpen(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed — try again.");
    } finally { setBusy(false); }
  };

  const onUpload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 600 * 1024) {
      setErr("Image too large — please pick something under 600 KB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => save(reader.result);
    reader.readAsDataURL(f);
  };

  return (
    <div className="absolute top-2 right-2 z-30" data-testid={`card-bg-picker-${userId}`}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        data-testid={`card-bg-picker-toggle-${userId}`}
        title="Customize card background"
        className="w-7 h-7 rounded-full bg-black/60 border border-white/20 text-white/80 hover:text-white hover:bg-black/80 text-[11px]"
      >
        <i className="fas fa-palette" />
      </button>
      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="absolute right-0 top-9 w-44 glass-strong rounded-xl p-2 border border-white/15 shadow-2xl"
          data-testid={`card-bg-picker-panel-${userId}`}
        >
          <p className="text-[9px] tracking-[3px] text-white/45 px-1 pb-1">PRESET</p>
          <div className="grid grid-cols-3 gap-1.5">
            {PRESETS.map((sw) => (
              <button
                key={sw.id}
                type="button"
                disabled={busy}
                onClick={() => save(`preset:${sw.id}`)}
                data-testid={`card-bg-preset-${sw.id}`}
                title={sw.label}
                className={`h-9 rounded-md border ${current === `preset:${sw.id}` ? "border-white" : "border-white/20"} hover:brightness-110`}
                style={{ background: sw.swatch }}
              />
            ))}
          </div>
          <label
            className="mt-2 block w-full text-[9px] tracking-[3px] text-center text-white/80 px-2 py-1.5 rounded-md bg-white/10 hover:bg-white/15 cursor-pointer"
            data-testid={`card-bg-upload-${userId}`}
          >
            <i className="fas fa-upload mr-1" />UPLOAD
            <input type="file" accept="image/*" className="hidden" onChange={onUpload} />
          </label>
          {current && (
            <button
              type="button"
              disabled={busy}
              onClick={() => save("")}
              data-testid={`card-bg-clear-${userId}`}
              className="mt-1.5 w-full text-[9px] tracking-[3px] text-[#14b8a6] hover:text-[#14b8a6] py-1"
            >
              CLEAR
            </button>
          )}
          {err && (
            <p
              data-testid={`card-bg-error-${userId}`}
              className="mt-1 text-[10px] text-[#14b8a6] leading-tight"
            >
              {err}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
